import React, { useEffect } from "react";
import { Container, Typography } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import "./HowProjectionsWork.css";

export const HOW_PROJECTIONS_PATH = "/how-projections-work";

const SECTIONS = [
    { id: "question", title: "The question" },
    { id: "numbers", title: "What the numbers mean" },
    { id: "stages", title: "Draft-stage models" },
    { id: "inputs", title: "What goes in" },
    { id: "built", title: "How it is built" },
    { id: "nhle", title: "NHL-equivalent stats" },
    { id: "preview", title: "Team preview boards" },
    { id: "not", title: "What this is not" },
];

function HowProjectionsWork() {
    useEffect(() => {
        if (!window.location.hash) return;
        const id = window.location.hash.slice(1);
        const timer = window.setTimeout(() => {
            document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 80);
        return () => window.clearTimeout(timer);
    }, []);

    return (
        <>
            <Helmet>
                <title>How Prospect Projections Work - Hockey Pipeline</title>
                <meta
                    name="description"
                    content="How Hockey Pipeline estimates when a prospect will play in the NHL, what M0–M5 means, and how NHL-equivalent stats and team preview boards fit together."
                />
                <link rel="canonical" href="https://hockeypipeline.com/how-projections-work" />
            </Helmet>
            <Container maxWidth="md" className="how-projections-page">
                <section className="home-panel how-projections-hero">
                    <Typography className="how-projections-eyebrow" component="p">
                        Methodology
                    </Typography>
                    <Typography variant="h4" component="h1" className="how-projections-title">
                        How prospect projections work
                    </Typography>
                    <Typography className="how-projections-lede">
                        Hockey Pipeline’s arrival model answers a narrow question: for a player who
                        has not yet debuted, how likely are they to play at least one NHL
                        regular-season game this season, next season, and the two after that?
                    </Typography>
                </section>

                <nav className="home-panel how-projections-toc" aria-label="On this page">
                    {SECTIONS.map((section) => (
                        <a key={section.id} href={`#${section.id}`} className="how-projections-toc-link">
                            {section.title}
                        </a>
                    ))}
                </nav>

                <article className="home-panel how-projections-article">
                    <section id="question" className="how-projections-section">
                        <h2>The question</h2>
                        <p>
                            Every June a few hundred players get drafted, and a much larger group of
                            undrafted teenagers keep chasing the league. Fans, scouts, and front
                            offices ask the same thing at slightly different times: will this player
                            play in the NHL, and if so, when?
                        </p>
                        <p>
                            This model is a <strong>timing</strong> model. It is not a ranking of
                            “best prospect,” and it is not a projection of points, ice time, or
                            career length. Given who the player is right now, it estimates the chance
                            of NHL play in each of the next four seasons.
                        </p>
                        <p>
                            A player is eligible only while they are still <strong>pre-debut</strong>.
                            The first regular-season NHL game takes them out of the arrival view.
                            “Played in the NHL” is a low bar on purpose: one regular-season game.
                            Playoffs do not count. The point is arrival, not starring.
                        </p>
                    </section>

                    <section id="numbers" className="how-projections-section">
                        <h2>What the numbers mean</h2>
                        <p>
                            From the start of a given season, each scored player has four probabilities:
                        </p>
                        <div className="how-projections-table-wrap">
                            <table className="how-projections-table">
                                <thead>
                                    <tr>
                                        <th>On the site</th>
                                        <th>Meaning</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>This season</td>
                                        <td>Chance of ≥1 NHL regular-season game in the current season</td>
                                    </tr>
                                    <tr>
                                        <td>Next season</td>
                                        <td>The same, one year later</td>
                                    </tr>
                                    <tr>
                                        <td>+2 / +3 seasons</td>
                                        <td>Two and three seasons out</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <p>
                            Those season numbers do <em>not</em> have to add to 100%. A player who
                            is already close can be high in several years in a row. A cup of coffee
                            in year three only would be low, low, then a bump, then low again.
                        </p>
                        <p>
                            The model that matters most is the player’s <strong>current stage</strong>:
                            M0 right after the draft, M1 a year later, M3 three years out, and so on.
                            That is the most recently applicable model for where they are in their
                            post-draft timeline.
                        </p>
                        <p>
                            <strong>Class rank</strong> orders players in the same draft class at
                            that same stage, by this-season NHL arrival probability. Use it to
                            compare peers, not to compare a 2026 draftee to a 2021 draftee who is
                            still waiting.
                        </p>
                        <p>
                            You will see these numbers on a player’s post-draft{" "}
                            <strong>NHL Arrival</strong> tab and on the{" "}
                            <Link to="/arrival-rankings">NHL Arrival Rankings</Link> board.
                        </p>
                    </section>

                    <section id="stages" className="how-projections-section">
                        <h2>Why there are six models</h2>
                        <p>
                            A 17-year-old on draft day and a 22-year-old still in the AHL are not
                            the same problem. The first has almost no professional track record. The
                            second has several post-draft seasons, and the fact they still have not
                            debuted is itself information.
                        </p>
                        <p>
                            So we train six draft-stage models, <strong>M0 through M5</strong>:
                        </p>
                        <div className="how-projections-table-wrap">
                            <table className="how-projections-table">
                                <thead>
                                    <tr>
                                        <th>Model</th>
                                        <th>Vantage point</th>
                                        <th>What it can see</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>M0</td>
                                        <td>Draft year</td>
                                        <td>Pre-draft production, draft slot, public-board path, size, org context</td>
                                    </tr>
                                    <tr>
                                        <td>M1</td>
                                        <td>+1 year</td>
                                        <td>Everything in M0, plus the first post-draft season</td>
                                    </tr>
                                    <tr>
                                        <td>M2</td>
                                        <td>+2 years</td>
                                        <td>Plus the second post-draft season</td>
                                    </tr>
                                    <tr>
                                        <td>M3–M5</td>
                                        <td>+3 to +5 years</td>
                                        <td>Accumulating post-draft seasons. M5 also covers anyone still pre-NHL after that.</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <p>
                            A player is scored by the stage that matches how long it has been since
                            their draft. A fresh draftee is M0. Someone drafted two years ago who
                            has not debuted is M2. Later stages are a selected group: the players
                            who were going to walk into the league on the first try have already
                            left the sample.
                        </p>
                    </section>

                    <section id="inputs" className="how-projections-section">
                        <h2>What goes in</h2>
                        <p>
                            Inputs are whatever would have been known at the start of the season
                            being predicted. Nothing from that season itself is allowed in.
                        </p>
                        <h3>Draft slot</h3>
                        <p>
                            Overall NHL draft slot, not pick-within-round. First overall is 1.
                            The first pick of the second round is 33 in a 32-pick first round.
                            Undrafted players are scored too, but history says NHL arrival in a
                            four-year window is rare for that group.
                        </p>
                        <h3>Production, translated</h3>
                        <p>
                            Junior, college, and European boxcars are not comparable raw. Goals,
                            assists, and points are scaled by league NHL-equivalent factors.
                            Games played stay as games. If a league has no translation, production
                            is heavily discounted rather than treated as NHL-caliber.
                        </p>
                        <h3>The org they have to beat</h3>
                        <p>
                            We look at the player’s NHL rights team (or draft team) and how deep
                            that club was <em>the prior season</em> at their position group —
                            forwards, defense, or goalies. Using the same season as the prediction
                            would leak the answer: the players who blocked him this year are part
                            of whether he played. This is most useful for “how hard is it to crack
                            this lineup now?” It cannot roll a roster forward through trades and
                            injuries.
                        </p>
                        <h3>The player</h3>
                        <p>Age at the snapshot, height, weight, handedness, and position.</p>
                        <h3>Public boards, and whether they were rising or falling</h3>
                        <p>
                            Draft slot already captures a lot of what scouts and lists agree on.
                            The more interesting piece is the <strong>path</strong>: rank about a
                            hundred days before the draft, about thirty days before, and on draft
                            day. A player who climbed from 50th in March to 20th in June is a
                            different story from one who was hyped early and cooled into the draft.
                        </p>
                    </section>

                    <section id="built" className="how-projections-section">
                        <h2>How it is built</h2>
                        <p>
                            Training examples are snapshots of players who had not debuted yet, and
                            only when the next four seasons have already been played out in real
                            life. Recent draftees are scored; they are not used as finished labels.
                            The target for each year is simple: did they play at least one NHL
                            regular-season game that season? That is not the same as “did they
                            debut that year.” Someone who arrives in year two and sticks will be
                            no, then yes, yes, yes.
                        </p>
                        <p>
                            Each stage is four small tree models, one per season. Later years can
                            see the previous year’s probability, so adjacent seasons are not
                            allowed to ignore each other. A few relationships are constrained in
                            the obvious direction: holding everything else fixed, a better draft
                            pick or a rising board trajectory should not hurt predicted NHL odds.
                        </p>
                        <p>
                            After those models fire, two edits make the published curve something
                            you can read:
                        </p>
                        <ul>
                            <li>
                                <strong>Historical pick rates.</strong> If an early pick comes in
                                below how those slots have typically arrived in the modern draft
                                era, we lift toward that history. We do not manufacture NHL odds
                                for late picks.
                            </li>
                            <li>
                                <strong>A single-hump shape.</strong> Four independent guesses
                                bounce. Real arrival tends to rise then fall, or start high and
                                ease off. We map the four-year path onto that family of shapes.
                            </li>
                        </ul>
                        <p>
                            Validation is time-aware: later draft classes are held out rather than
                            shuffled in with earlier ones. The live question is always “what about
                            this class?” The numbers are most useful for ordering players and
                            sketching a path. Treat a 37% versus a 38% as a tie.
                        </p>
                    </section>

                    <section id="nhle" className="how-projections-section">
                        <h2>NHL-equivalent stats</h2>
                        <p>
                            On a player’s Stats tab, Raw is what they actually scored.{" "}
                            <strong>NHL-E</strong> multiplies goals, assists, and points by that
                            league-season’s translation factor so an OHL point and an NCAA point
                            are not treated as the same thing. Games stay unscaled. The factor
                            column is the multiplier itself.
                        </p>
                        <p>
                            The arrival model uses the same translation underneath. NHL-E on the
                            stats table is the human-readable version of that idea.
                        </p>
                    </section>

                    <section id="preview" className="how-projections-section">
                        <h2>Team preview boards</h2>
                        <p>
                            <Link to="/prospect-preview">Prospect Preview</Link> ranks players
                            currently under a team’s NHL rights. Arrival probabilities (NHL next
                            season, any-of-window NHL play) sit beside translated scoring and
                            other prospect-model fields you may see on those tables: board score,
                            projected peak value, and broader outcome labels such as impact or
                            bust. Those extra columns are complementary signals, not a second copy
                            of the four-year arrival curve.
                        </p>
                        <p>
                            Default sort on a team board leans on blended rank, then board score
                            and peak value, so the list is a pool ranking — not only “who plays
                            an NHL game soonest.”
                        </p>
                    </section>

                    <section id="not" className="how-projections-section">
                        <h2>What this is not</h2>
                        <ul>
                            <li>
                                Not P(this is the year they debut) as a set of slices that add to
                                100%. It is P(play that season).
                            </li>
                            <li>
                                Not a quality projection. A nine-game look and a 40-goal season
                                both count as a 1.
                            </li>
                            <li>
                                Not a crystal ball for injuries, trades, or a promised tryout. Org
                                depth is last year’s snapshot.
                            </li>
                            <li>
                                Not the historical debut-timing charts on{" "}
                                <Link to="/draft-debuts">NHL Debut Timing</Link>. Those are
                                actuals for past draft classes, not this player’s forecast.
                            </li>
                        </ul>
                        <p className="how-projections-footnote">
                            Methodology can change as the model is retrained. Numbers on player
                            pages and rankings always reflect the latest published snapshot.
                        </p>
                    </section>
                </article>
            </Container>
        </>
    );
}

export default HowProjectionsWork;
